import React from "react";
import { Button } from "@/components/ui/button";
import { FileText, Trash2 } from "lucide-react";
import { ACTION_LABELS, ACTION_COLORS } from "./constants";

const formatDateTime = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const renderChanges = (changes) => {
  if (!changes || Object.keys(changes).length === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      {Object.entries(changes).map(([field, val]) => (
        <div key={field} className="text-xs text-gray-300 bg-[#0f0f10] rounded px-2 py-1 border border-red-900/20">
          <span className="font-semibold text-gray-200">{field}:</span>{" "}
          <span className="text-red-300 line-through">{String(val?.old ?? "—")}</span>{" "}
          <span className="text-gray-500">→</span>{" "}
          <span className="text-green-300">{String(val?.new ?? "—")}</span>
        </div>
      ))}
    </div>
  );
};

export const HistoryTimeline = ({ logs, loading, isSuperadmin, onDelete }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent"></div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
        <p>Tidak ada history untuk filter ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs.map((entry) => (
        <div
          key={entry.id}
          className="p-4 rounded-lg border border-red-900/20 bg-[#0f0f10]/60 hover:border-red-800/40 transition-colors"
          data-testid={`history-entry-${entry.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${ACTION_COLORS[entry.action] || "bg-gray-700 text-gray-200 border-gray-600"}`}>
                  {ACTION_LABELS[entry.action] || entry.action}
                </span>
                <span className="text-white font-semibold truncate">{entry.member_name || entry.member_id}</span>
                <span className="text-xs text-gray-500">oleh <b className="text-gray-300">{entry.actor_name}</b></span>
              </div>
              <div className="text-xs text-gray-500">{formatDateTime(entry.created_at)}</div>
              {entry.note && (
                <div className="mt-2 text-sm text-gray-200 bg-[#1a1a1b] border border-purple-900/30 rounded px-3 py-2 italic">
                  "{entry.note}"
                </div>
              )}
              {renderChanges(entry.changes)}
            </div>
            {isSuperadmin && (
              <Button
                onClick={() => onDelete(entry.id)}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-900/20 flex-shrink-0"
                title="Hapus entri ini"
                data-testid={`delete-history-${entry.id}`}
              >
                <Trash2 size={16} />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HistoryTimeline;
